// Apps Panel - For selecting and managing apps in the editor
import { useState } from 'react';
import { Plus, Settings, Trash2, Calendar, Send } from 'lucide-react';
import { APP_DEFINITIONS, APP_TYPES } from './appConstants';

// Color schemes for app types
const APP_COLOR_SCHEMES = {
  [APP_TYPES.PITCH]: {
    bg: '#F0FDF4',
    bgHover: '#DCFCE7',
    border: '#BBF7D0',
    iconBg: '#22C55E',
    text: '#166534',
    textLight: '#15803D',
  },
  [APP_TYPES.MEETING]: {
    bg: '#EFF6FF',
    bgHover: '#DBEAFE',
    border: '#BFDBFE',
    iconBg: '#3B82F6',
    text: '#1E40AF',
    textLight: '#2563EB',
  },
};

// Icon component for each app type
const AppTypeIcon = ({ type, size = 20 }) => {
  if (type === APP_TYPES.MEETING) {
    return <Calendar className="text-white" style={{ width: size, height: size }} />;
  }
  return <Send className="text-white" style={{ width: size, height: size }} />;
};

export default function AppsPanel({ 
  apps = [], 
  onAddApp, 
  onEditApp, 
  onRemoveApp,
  isFloating = true 
}) {
  const [hoveredApp, setHoveredApp] = useState(null);

  // Check if an app type is already added
  const isAppAdded = (type) => apps.some(a => a.type === type);

  // Get enabled apps count
  const enabledCount = apps.filter(a => a.enabled).length;

  return (
    <div style={{
      width: isFloating ? '320px' : '100%',
      background: 'rgba(255,255,255,0.98)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      boxShadow: isFloating ? '0 12px 40px rgba(0,0,0,0.12)' : 'none',
      border: isFloating ? '1px solid rgba(0,0,0,0.05)' : 'none',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        borderBottom: '1px solid #F1F5F9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(to bottom, #FAFBFC, #FFFFFF)'
      }}>
        <div>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>Apps</span>
          {enabledCount > 0 && (
            <span style={{ 
              marginLeft: '8px',
              padding: '2px 8px',
              background: '#E0E7FF',
              color: '#4F46E5',
              fontSize: '11px',
              fontWeight: '500',
              borderRadius: '100px'
            }}>
              {enabledCount} active
            </span>
          )}
        </div>
      </div>

      {/* Available Apps */}
      <div style={{ padding: '12px 14px' }}>
        <p style={{ fontSize: '11px', color: '#64748B', marginBottom: '10px', fontWeight: '500' }}>
          Add interactive features to your profile
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Object.values(APP_DEFINITIONS).map((appDef) => {
            const isAdded = isAppAdded(appDef.type);
            const existingApp = apps.find(a => a.type === appDef.type);
            const isHovered = hoveredApp === appDef.type;
            const colors = APP_COLOR_SCHEMES[appDef.type] || APP_COLOR_SCHEMES[APP_TYPES.PITCH];

            return (
              <div
                key={appDef.type}
                onMouseEnter={() => setHoveredApp(appDef.type)}
                onMouseLeave={() => setHoveredApp(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: `1px solid ${isAdded ? colors.border : '#E2E8F0'}`,
                  background: isAdded ? colors.bg : (isHovered ? '#F8FAFC' : '#FFFFFF'),
                  transition: 'all 0.2s ease',
                }}
              >
                {/* Icon */}
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: isAdded ? colors.iconBg : '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {isAdded ? (
                    <AppTypeIcon type={appDef.type} size={18} />
                  ) : (
                    <span style={{ fontSize: '20px' }}>{appDef.icon}</span>
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: isAdded ? colors.text : '#1E293B',
                    marginBottom: '2px'
                  }}>
                    {appDef.name}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: isAdded ? colors.textLight : '#64748B'
                  }}>
                    {appDef.description}
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  {isAdded ? (
                    <>
                      <button
                        onClick={() => onEditApp(existingApp)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#FFFFFF',
                          color: colors.text,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease'
                        }}
                        title="Edit app"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onRemoveApp(existingApp)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#FFFFFF',
                          color: '#EF4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease'
                        }}
                        title="Remove app"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => onAddApp(appDef)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        background: colors.iconBg,
                        color: '#FFFFFF',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <Plus className="w-3 h-3" />
                      Add
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer hint */}
      <div style={{
        padding: '12px 18px',
        borderTop: '1px solid #F1F5F9',
        background: '#FAFBFC'
      }}>
        <p style={{ fontSize: '11px', color: '#94A3B8', textAlign: 'center' }}>
          Apps add interactive buttons to your profile
        </p>
      </div>
    </div>
  );
}
